/* eslint-disable @typescript-eslint/no-unused-vars */

import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import ReactMarkdown, { type Options as ReactMarkdownOptions } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { CollapsibleThink } from './CollapsibleThink';
import { CodeHighlighter } from './CodeHighlighter';
import { ImageWrapper } from './ImageWrapper';
import { FormulaRenderer, remarkLatex } from './FormulaRenderer';
import { ImgCopy } from './Images/ImgCopy';
import type { AttachmentMeta } from '../models';

// Whitelist of HTML tags that rehypeRaw should process
// Other tags will be escaped and displayed as text
const ALLOWED_HTML_TAGS = new Set([
  // Table elements
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col', 'caption',
  // List elements
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  // Inline elements
  'br', 'hr', 'wbr',
  // Text formatting (commonly used in markdown)
  'sub', 'sup', 'mark', 'ins', 'del', 's', 'u',
  // Block elements
  'div', 'span', 'p',
  // Details/summary
  'details', 'summary',
  // Media (handled by markdown but may appear in raw HTML)
  'img', 'a',
  // Code blocks (must be preserved)
  'pre', 'code',
]);

/**
 * Escapes non-whitelisted HTML tags in text content, but preserves tags inside inline code (`text`).
 * Code blocks (```...```) are still escaped because CodeHighlighter uses dangerouslySetInnerHTML
 * and would render raw HTML tags. Inline code uses React text nodes which handle escaping natively.
 */
function escapeNonWhitelistedHtmlTags(text: string): string {
  // First, protect inline code spans by replacing them with placeholders.
  // Match inline code but NOT code blocks (triple backticks).
  // Negative lookbehind/lookahead for ` ensures we only match single-backtick spans.
  const inlineCodeSpans: string[] = [];
  const withPlaceholders = text.replace(/(?<!`)(`)(?!`)(.+?)(?<!`)\1(?!`)/g, (match) => {
    inlineCodeSpans.push(match);
    return `\x00INLINE_CODE_${inlineCodeSpans.length - 1}\x00`;
  });

  // Escape non-whitelisted HTML tags in the remaining text (including inside code blocks)
  const escaped = withPlaceholders.replace(/<(\/?)\s*([a-zA-Z][a-zA-Z0-9-]*)((?:\s+[^>]*)?)\s*(\/?)>/g, 
    (match, _ /* closingSlash */, tagName/*, attributes, selfClosing*/) => {
      const lowerTagName = tagName.toLowerCase();
      
      // If tag is whitelisted, keep it as-is
      if (ALLOWED_HTML_TAGS.has(lowerTagName)) {
        return match;
      }
      
      // Otherwise, escape the angle brackets
      return match.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  );

  // Restore inline code spans (unescaped)
  return escaped.replace(/\x00INLINE_CODE_(\d+)\x00/g, (_, index) => inlineCodeSpans[Number(index)]);
}

interface MarkdownRendererProps {
  isGenerating: boolean;
  content: string;
  think?: string;
  maxLength?: number;
  enableFormatting: boolean;
  thinkingTimeTaken?: number | null; // Time taken for thinking, if applicable
  attachmentMetas?: AttachmentMeta[]; // Pre-loaded attachment metadata for image dimension hints
  onUseImageAttachment?: (attachmentId: string) => void;
}

@observer
export class MarkdownRenderer extends React.Component<MarkdownRendererProps> {
  @observable _isExpanded: boolean = false;
  @observable _expandedCodeBlocks: Map<string, boolean> = new Map();
  private _markdownOptionsCache = new Map<string, ReactMarkdownOptions>();
  private _codeBlockIndex: number = 0;

  constructor(props: MarkdownRendererProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount(): void {
    // subscribe on theme changes and code highlighting
    document.addEventListener('themeChanged', this._onForceUpdate);
    document.addEventListener('codeHighlightingChanged', this._onForceUpdate);

    // re-render on window resize to adjust code block height
    window.addEventListener('resize', this._onForceUpdate);
  }

  componentWillUnmount(): void {
    document.removeEventListener('themeChanged', this._onForceUpdate);
    document.removeEventListener('codeHighlightingChanged', this._onForceUpdate);
    window.removeEventListener('resize', this._onForceUpdate);
  }

  private _onForceUpdate = () => this.forceUpdate();
  
  private processContent = (content: string) => {

    // Split content by <think> tags and process each part
    // This regex handles both closed and unclosed think tags
    const parts = content.split(/(<think>[\s\S]*?(?:<\/think>|$))/g);

    if (this.props.maxLength && content.length > this.props.maxLength && !this._isExpanded) {
      return this._getMarkdown(content.slice(0, this.props.maxLength));
    }
    
    return parts.map((part, index) => {
      // Check for closed think tag
      const closedThinkMatch = part.match(/<think>([\s\S]*?)<\/think>/);
      // Check for unclosed think tag (still thinking)
      const openThinkMatch = part.match(/<think>([\s\S]*)$/);
      
      if (closedThinkMatch) {
        return (
          <CollapsibleThink key={index} text="Think">
            <>{this._getMarkdown(closedThinkMatch[1].trim())}</>
          </CollapsibleThink>
        );
      } else if (openThinkMatch) {
        // Handle unclosed think block (still thinking)
        return (
          <CollapsibleThink key={index} text="Thinking...">
            <>{this._getMarkdown(openThinkMatch[1].trim())}</>
          </CollapsibleThink>
        );
      } else if (part.trim()) {
        return (
          <React.Fragment key={index}>{this._getMarkdown(part)}</React.Fragment>
        );
      }
      return null;
    }).filter(Boolean);
  };

  @action
  private _onShowMoreClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    this._isExpanded = !this._isExpanded;
  }

  @action
  private _toggleCodeBlock = (blockKey: string) => {
    const current = this._expandedCodeBlocks.get(blockKey) || false;
    this._expandedCodeBlocks.set(blockKey, !current);
    this.forceUpdate(); // Force re-render after map change
  }

  // Generate a stable key for a code block based on language and position
  private _getCodeBlockKey(language: string, blockIndex: number): string {
    // Use just language + index as a stable identifier
    // The index is stable during generation since new code blocks are appended at the end
    return `${language}:${blockIndex}`;
  }

  private get _getMarkdownOptions(): ReactMarkdownOptions {
    const isGenerating = this.props.isGenerating;
    const content = this.props.content || '';

    const expandedBlocksKey = Array.from(this._expandedCodeBlocks.entries()).map(([k, v]) => `${k}:${v}`).join(',');
    const cacheKey = `markdownOptions;${isGenerating};${this._isExpanded};${expandedBlocksKey}`;
    if (this._markdownOptionsCache.has(cacheKey)) {
      return this._markdownOptionsCache.get(cacheKey)!
    }

    const options: ReactMarkdownOptions = {
      rehypePlugins: [rehypeRaw, rehypeSanitize],
      remarkPlugins: [remarkLatex, remarkGfm],
      
      components: {
        p: ({ node, children, ...props }) => {
          const className = props.className ? `md-p ${props.className}` : 'md-p';

          const additionalNodes = [];
          if (this.props.maxLength && content.length > this.props.maxLength) {
            if (this._isExpanded) {
              additionalNodes.push(' ', <span key='more' className="show-more" onClick={this._onShowMoreClick}>(show less)</span>);
            } else {
              additionalNodes.push('... ', <span key='more' className="show-more" onClick={this._onShowMoreClick}>(show more)</span>);
            }
          }

          return React.createElement('p', { className, ...props }, [children, ...additionalNodes]);
        },
        h1: ({ node, ...props }) => React.createElement('h1', { className: 'md-h1', ...props }),
        h2: ({ node, ...props }) => React.createElement('h2', { className: 'md-h2', ...props }),
        h3: ({ node, ...props }) => React.createElement('h3', { className: 'md-h3', ...props }),
        strong: ({ node, ...props }) => React.createElement('strong', { className: 'md-bold', ...props }),
        em: ({ node, ...props }) => React.createElement('em', { className: 'md-italic', ...props }),
        a: ({ node, ...props }) => React.createElement('a', { className: 'md-link', target: '_blank', rel: 'noopener noreferrer', ...props }),
        ul: ({ node, ...props }) => React.createElement('ul', { className: 'md-list', ...props }),
        ol: ({ node, ...props }) => React.createElement('ol', { className: 'md-olist', ...props }),
        li: ({ node, ...props }) => React.createElement('li', { className: 'md-li', ...props }),
        table: ({ node, ...props }) => React.createElement('table', { className: 'md-table', ...props }),
        thead: ({ node, ...props }) => React.createElement('thead', { className: 'md-thead', ...props }),
        tbody: ({ node, ...props }) => React.createElement('tbody', { className: 'md-tbody', ...props }),
        tr: ({ node, ...props }) => React.createElement('tr', { className: 'md-tr', ...props }),
        td: ({ node, ...props }) => React.createElement('td', { className: 'md-td', ...props }),
        th: ({ node, ...props }) => React.createElement('th', { className: 'md-th', ...props }),
        img: ({ node, ...props }) => {
          const src = (props as { [key: string]: unknown })['src'] as string;
          
          // Look up dimensions from pre-loaded attachment metas for /image/{id} URLs
          let width: number | null | undefined = undefined;
          let height: number | null | undefined = undefined;
          const imageIdMatch = src?.match(/\/image\/([a-f0-9]{24})$/);
          if (imageIdMatch && this.props.attachmentMetas) {
            const meta = this.props.attachmentMetas.find(m => m.id === imageIdMatch[1]);
            if (meta) {
              width = meta.imageWidth;
              height = meta.imageHeight;
            }
          }
          
          const attachmentId = imageIdMatch?.[1];
          const useAttachment = attachmentId && this.props.onUseImageAttachment
            ? () => this.props.onUseImageAttachment!(attachmentId)
            : undefined;
          return <ImageWrapper key={props.key} className="message-image" src={src} alt={props.alt || ''} width={width} height={height} onUseImageAttachment={useAttachment} />;
        },
        span: ({ node, children, ...props }) => {
          const allProps = props as { [key: string]: unknown };
          
          // Check if this is a LaTeX formula span
          if (allProps.className && 
              (allProps.className as string).includes('latex-formula')) {
            
            const formulaData = allProps['data-formula'] as string;
            const displayMode = allProps['data-display-mode'] === 'true' || allProps['data-display-mode'] === true;
            
            if (formulaData) {
              const decodedFormula = decodeURIComponent(formulaData);
              return (
                <FormulaRenderer 
                  formula={decodedFormula} 
                  displayMode={displayMode}
                  className="md-formula"
                />
              );
            }
          }
          
          return React.createElement('span', props, children);
        },
        div: ({ node, children, ...props }) => {
          // Check if this is our think block
          if ((props as { [key: string]: unknown })['data-think']) {
            return <CollapsibleThink text="Think">{children}</CollapsibleThink>;
          }
          return React.createElement('div', props, children);
        },
        code: ({ node, className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className || '');
          const codeString = String(children).replace(/\n$/, '').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
          const isHtml = match && match[1].toLowerCase() === 'html';
          const lineCount = codeString.split('\n').length;
          const shouldCollapse = lineCount > 20;
          const currentBlockIndex = this._codeBlockIndex++;
          const blockKey = this._getCodeBlockKey(match?.[1] || '', currentBlockIndex);
          const isExpanded = this._expandedCodeBlocks.get(blockKey) || false;
          
          return match ? (
            <div className="code-block" style={{ position: 'relative' }}>
              <div 
                className={`code-block-wrapper ${shouldCollapse && !isExpanded ? 'collapsed' : ''}`}
                style={{
                  maxHeight: shouldCollapse && !isExpanded ? '400px' : 'none',
                  overflow: shouldCollapse && !isExpanded ? 'auto' : 'hidden',
                  position: 'relative'
                }}
                onWheel={(e) => {
                  // Allow scrolling when collapsed
                  if (shouldCollapse && !isExpanded) {
                    e.stopPropagation();
                  }
                }}
              >
                <CodeHighlighter 
                  code={codeString}
                  language={match[1]}
                  showLineNumbers={!isGenerating && window.innerWidth >= 768}
                />
              </div>
              {shouldCollapse && !isExpanded && (
                <div 
                  className="code-fade-overlay"
                  style={{
                    position: 'absolute',
                    bottom: 36,
                    left: 0,
                    right: 0,
                    height: '60px',
                    background: 'linear-gradient(to bottom, transparent, var(--formulas-bg))',
                    pointerEvents: 'none'
                  }}
                />
              )}
              {shouldCollapse && (
                <button
                  className="expand-collapse-button"
                  onClick={() => this._toggleCodeBlock(blockKey)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px',
                    marginTop: '4px',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textAlign: 'center'
                  }}
                  title={isExpanded ? 'Collapse code block' : 'Expand code block'}
                >
                  {isExpanded 
                    ? `▲ Collapse (${lineCount} lines)` 
                    : `▼ Expand (${lineCount} lines${isGenerating ? ' ...' : ', showing first 20'})`
                  }
                </button>
              )}
              <div className="code-block-buttons">
                {isHtml && (
                  <button
                    className="copy-button copy-button-code run-button"
                    onClick={() => {
                      // Create a secure blob URL for the HTML content
                      const blob = new Blob([codeString], { type: 'text/html' });
                      const blobUrl = URL.createObjectURL(blob);
                      // Open in new tab with noopener for security
                      window.open(blobUrl, '_blank', 'noopener,noreferrer');
                      // Revoke the blob URL after a short delay to free memory
                      // but give enough time for the page to load
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
                    }}
                    title="Run HTML in browser"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                  </button>
                )}
                <button
                  className="copy-button copy-button-code"
                  onClick={(e) => {
                    navigator.clipboard.writeText(codeString);
                    const button = e.currentTarget as HTMLButtonElement;
                    button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"> <path d="M20 6L8 18l-6-6"></path></svg>';
                    setTimeout(() => {
                      button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
                    }, 2000);
                  }}
                  title="Copy to clipboard"
                >
                  <ImgCopy />
                </button>
              </div>
            </div>
          ) : (
            React.createElement('code', { className: 'inline-code', ...props }, children)
          );
      },
    }};

    this._markdownOptionsCache.set(cacheKey, options);
    return options;
  };

  convertLatexToDoubleDollar(text: string) {
    const normalized = text.replace(/\r\n/g, '\n');
    const parts = normalized.split(/(\n```[^\n]*\n)/g);
    let isCodeBlock = false;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (part.trim().startsWith('```') && !isCodeBlock) {
        isCodeBlock = true;
        continue;
      }

      if (part.trim() === '```' && isCodeBlock) {
        isCodeBlock = false;
        continue;
      }

      if (!isCodeBlock) {

        // Convert display math: \[ content \] to $$content$$
        parts[i] = parts[i].replace(/\\\[([\s\S]*?)\\\]/g, '$$$$$1$$$$');
        
        // Convert inline math: \( content \) to $content$
        parts[i] = parts[i].replace(/\\\(([\s\S]*?)\\\)/g, '$$$1$$');
      }
    }

    // -------------------------------------------------------------------------
    // 4️⃣  Re‑assemble the document.
    // -------------------------------------------------------------------------
    return parts.join('');
  }

  private _getMarkdown(content: string) {
    const convertedText = this.convertLatexToDoubleDollar(content);
    const escapedText = escapeNonWhitelistedHtmlTags(convertedText);
    return (
      <ReactMarkdown
        {...this._getMarkdownOptions}
      >
        {escapedText}
      </ReactMarkdown>
    );
  }

  render(){
    // Reset code block index at the start of each render
    this._codeBlockIndex = 0;
    
    const { content, think, isGenerating, thinkingTimeTaken, enableFormatting } = this.props;

    const isThinking = !!think && think.trim().length > 0 && !content && isGenerating;
    let collapsedTitle = isThinking ? 'Thinking' : 'Think';
    if (thinkingTimeTaken && !isThinking) {
      collapsedTitle = `Thought for ${(thinkingTimeTaken / 1_000).toFixed(2)}s`;
    }

    if (!enableFormatting){
      return (
        <code>
            {think && <CollapsibleThink text={collapsedTitle} className={`${isThinking ? 'active' : ''}`}>
            {`<think>${think}</think>`}
            </CollapsibleThink>}
            {content}
        </code>
      );
    }

    return (
      <div className="markdown-renderer">
        {think && <CollapsibleThink text={collapsedTitle} className={`${isThinking ? 'active' : ''}`}>
          <>{this._getMarkdown(think)}</>
        </CollapsibleThink>}
        {this.processContent(content)}
      </div>
    );
  }
}

// Type declaration for Prism
declare global {
  interface Window {
    Prism: {
      highlight: (text: string, grammar: unknown, language: string) => string;
      languages: { [key: string]: unknown };
    };
  }
}